import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { Activity, CheckCircle2, AlertTriangle, XCircle, Wrench, Cog, MapPin, Clock, ArrowUpDown, Filter, Hourglass, CircleDashed } from 'lucide-react';

const HEALTH_CONFIG = {
  green: { label: 'Healthy', icon: CheckCircle2, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', progress: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  amber: { label: 'Warning', icon: AlertTriangle, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', progress: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  red: { label: 'Overdue', icon: XCircle, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', progress: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
  learning: { label: 'Building Baseline', icon: Hourglass, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', progress: 'bg-blue-400', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  idle: { label: 'Not Installed', icon: CircleDashed, bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', progress: 'bg-slate-300', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function MachineCard({ machine }) {
  const cfg = HEALTH_CONFIG[machine.health] || HEALTH_CONFIG.idle;
  const Icon = cfg.icon;
  const hasUsage = machine.has_active_cycle && machine.cycle_pct !== null && machine.cycle_pct !== undefined;
  const barWidth = hasUsage ? Math.min(machine.cycle_pct, 100) : 0;

  return (
    <Card className={`p-5 border ${cfg.border} hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`} data-testid={`machine-card-${machine.machine_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center`}>
            <Icon size={16} className={cfg.text} />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 leading-tight">{machine.brand} {machine.model}</p>
            <p className="text-xs text-slate-400 font-mono">{machine.serial_number}</p>
          </div>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.badge}`} data-testid={`health-badge-${machine.machine_id}`}>
          {cfg.label}
        </span>
      </div>

      <div className="flex items-center gap-1.5 mb-3">
        <MapPin size={12} className="text-slate-400" />
        <span className="text-xs text-slate-500 truncate">{machine.site_name || 'Unassigned'}</span>
      </div>

      {/* Cycle Progress — only when there's an active cycle with a learned baseline */}
      {hasUsage ? (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Cycle Usage</span>
            <span className={`text-xs font-bold ${cfg.text}`}>{machine.cycle_pct}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full ${cfg.progress} transition-all duration-700`} style={{ width: `${barWidth}%` }} />
          </div>
        </div>
      ) : machine.has_active_cycle ? (
        <div className="mb-3 text-[11px] text-blue-600 bg-blue-50 rounded-md px-3 py-2 flex items-center gap-1.5">
          <Hourglass size={12} /> Building baseline — usage shown once cycles close
        </div>
      ) : (
        <div className="mb-3 text-[11px] text-slate-500 bg-slate-50 rounded-md px-3 py-2 flex items-center gap-1.5">
          <CircleDashed size={12} /> No cycle usage — awaiting installation
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="bg-slate-50 rounded-md py-1.5 px-1">
          <p className="text-[10px] text-slate-400">Hours Used</p>
          <p className="text-xs font-bold text-slate-700">{hasUsage ? machine.hours_used?.toLocaleString() : '—'}</p>
        </div>
        <div className="bg-slate-50 rounded-md py-1.5 px-1">
          <p className="text-[10px] text-slate-400">Days Active</p>
          <p className="text-xs font-bold text-slate-700">{machine.has_active_cycle ? machine.days_active : '—'}</p>
        </div>
        <div className="bg-slate-50 rounded-md py-1.5 px-1">
          <p className="text-[10px] text-slate-400">Avg Life</p>
          <p className="text-xs font-bold text-slate-700">{machine.avg_cycle_life > 0 ? `${machine.avg_cycle_life}h` : '—'}</p>
        </div>
      </div>

      {!machine.has_active_cycle ? (
        <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 mb-3 flex items-center gap-1.5">
          <XCircle size={12} /> No active cycle — needs installation
        </div>
      ) : (
        <p className="text-[10px] text-slate-400 mb-3">Installed: {machine.installation_date}</p>
      )}

      <Link to="/change-out" className="block">
        <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800 text-xs" data-testid={`changeout-btn-${machine.machine_id}`}>
          <Wrench size={12} className="mr-1.5" /> {machine.has_active_cycle ? 'Change-Out' : 'New Installation'}
        </Button>
      </Link>
    </Card>
  );
}

export default function FleetStatusDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState('all');
  const [sortBy, setSortBy] = useState('urgency');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/fleet/status').catch(() => ({ data: { total: 0, green: 0, amber: 0, red: 0, machines: [] } })),
      api.get('/sites').catch(() => ({ data: [] })),
    ]).then(([fleet, s]) => {
      setData(fleet.data);
      setSites(s.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const fetchFleet = async (sid) => {
    setLoading(true);
    try {
      const res = await api.get(`/fleet/status${sid && sid !== 'all' ? `?site_id=${sid}` : ''}`);
      setData(res.data);
    } catch (err) { console.warn('fleet status load failed', err); } finally { setLoading(false); }
  };

  const handleSiteChange = (val) => {
    setSiteFilter(val);
    fetchFleet(val);
  };

  const machines = data?.machines || [];
  const sorted = [...machines].sort((a, b) => {
    if (sortBy === 'urgency') {
      const ord = { red: 0, amber: 1, learning: 2, green: 3, idle: 4 };
      return (ord[a.health] ?? 5) - (ord[b.health] ?? 5) || (b.cycle_pct || 0) - (a.cycle_pct || 0);
    }
    if (sortBy === 'name') return `${a.brand} ${a.serial_number}`.localeCompare(`${b.brand} ${b.serial_number}`);
    if (sortBy === 'site') return (a.site_name || '').localeCompare(b.site_name || '');
    return 0;
  });

  const kpis = [
    { label: 'Total Machines', value: data?.total || 0, icon: Cog, color: 'text-slate-600 bg-slate-100', testId: 'kpi-total' },
    { label: 'Healthy', value: data?.green || 0, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50', testId: 'kpi-green' },
    { label: 'Warning', value: data?.amber || 0, icon: AlertTriangle, color: 'text-amber-600 bg-amber-50', testId: 'kpi-amber' },
    { label: 'Overdue', value: data?.red || 0, icon: XCircle, color: 'text-red-600 bg-red-50', testId: 'kpi-red' },
    { label: 'Not Installed', value: data?.not_installed || 0, icon: CircleDashed, color: 'text-slate-500 bg-slate-50', testId: 'kpi-not-installed' },
  ];

  return (
    <div data-testid="fleet-status-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><Activity size={22} /> Fleet Status</h1>
          <p className="text-sm text-slate-500 mt-1">Machine health overview — identify which excavators need attention</p>
        </div>
        {data && (
          <div className="flex flex-col items-start sm:items-end gap-1 text-xs text-slate-400" data-testid="avg-life-indicator">
            {data.learning_active ? (
              <span className="flex items-center gap-1.5 text-blue-600"><Hourglass size={12} /> Avg cycle life: <span className="font-semibold">learning — not enough data yet</span></span>
            ) : (
              <span className="flex items-center gap-1.5"><Clock size={12} /> Avg cycle life: <span className="font-semibold text-slate-600">{data.avg_cycle_life} hrs</span> <span className="text-slate-300">· self-learning ({data.cycles_learned} cycles)</span></span>
            )}
            {data.learning > 0 && <span className="text-blue-500">{data.learning} machine{data.learning > 1 ? 's' : ''} building baseline</span>}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="p-4 flex items-center gap-3" data-testid={k.testId}>
              <div className={`w-10 h-10 rounded-lg ${k.color} flex items-center justify-center`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 leading-tight">{loading ? '-' : k.value}</p>
                <p className="text-xs text-slate-500">{k.label}</p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Filter size={12} /> Filter:
          </div>
          <Select value={siteFilter} onValueChange={handleSiteChange}>
            <SelectTrigger className="w-48 text-xs bg-white" data-testid="filter-site">
              <SelectValue placeholder="All Sites" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sites</SelectItem>
              {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <ArrowUpDown size={12} /> Sort:
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 text-xs bg-white" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="urgency">By Urgency</SelectItem>
              <SelectItem value="name">By Name</SelectItem>
              <SelectItem value="site">By Site</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Machine Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <div key={i} className="h-64 bg-slate-100 rounded-lg animate-pulse" />)}
        </div>
      ) : sorted.length === 0 ? (
        <Card className="p-10 text-center">
          <Cog size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No machines found for the selected filter.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {sorted.map(m => <MachineCard key={m.machine_id} machine={m} />)}
        </div>
      )}
    </div>
  );
}
